// Slug → Schema.org `MedicalCondition` metadata for the CMS-authored
// expertise pages.  Emitted on `/expertise/{slug}` so search engines
// (and Google's Knowledge Graph "about this condition" cards) can
// resolve the page to a specific clinical condition with codes.
//
// Only slugs that map to genuine MedicalConditions are listed;
// procedure-only pages (e.g. "cataract-surgery") stay on
// MedicalProcedure and don't appear here.
//
// Values verified against ICD-10 (WHO) and SNOMED CT (v20240501).
// If you're expanding this file, cross-check the code before shipping —
// wrong codes are worse for E-E-A-T than no codes.

export const CONDITION_META = {
  "cataract-surgery": {
    condition: {
      name: "Cataract",
      alternateName: ["Cataracta", "Motiabind"],
      icd10: "H25",
      snomed: "193570009",
      anatomy: "Crystalline lens",
      prognosis: "Excellent visual recovery in the majority of cases when the eye is otherwise healthy; surgery is elective and outcome-dependent on IOL choice and pre-op ocular co-morbidity.",
    },
  },
  "premium-iol": {
    condition: {
      name: "Cataract (premium IOL indication)",
      icd10: "H25",
      snomed: "193570009",
      anatomy: "Crystalline lens",
      prognosis: "Multifocal / EDOF / toric IOLs are designed to significantly reduce spectacle dependence for suitable candidates; outcomes depend on ocular surface health, macular status and refractive stability.",
    },
  },
  "medical-retina": {
    condition: {
      name: "Retinal disease (macular, diabetic and vascular)",
      icd10: "H35",
      snomed: "30366006",
      anatomy: "Retina",
      prognosis: "Long-term outcome depends on early diagnosis, systemic control (glycaemia, blood pressure) and adherence to injection or laser therapy schedules.",
    },
  },
  "age-related-macular-degeneration": {
    condition: {
      name: "Age-related Macular Degeneration (AMD)",
      alternateName: ["ARMD", "AMD", "Macular Degeneration"],
      icd10: "H35.3",
      snomed: "267718000",
      anatomy: "Macula",
      prognosis: "Dry AMD is slowly progressive and managed conservatively with AREDS-2 supplementation. Wet AMD requires prompt ophthalmology evaluation as delayed treatment increases the risk of permanent central vision loss.",
    },
  },
  "diabetic-retinopathy": {
    condition: {
      name: "Diabetic Retinopathy",
      alternateName: ["DR", "Diabetic Eye Disease"],
      icd10: "E11.359",
      snomed: "4855003",
      anatomy: "Retina",
      prognosis: "Progression is strongly correlated with glycaemic and blood-pressure control. Annual dilated screening, timely anti-VEGF and PRP laser preserve vision in the majority of cases.",
    },
  },
  "squint-strabismus": {
    condition: {
      name: "Strabismus (Squint)",
      alternateName: ["Squint", "Crossed Eyes", "Wandering Eye", "Bhengapan"],
      icd10: "H50",
      snomed: "22066006",
      anatomy: "Extraocular muscles",
      prognosis: "Paediatric squint outcomes are best with treatment before 8 years of age. Adult squint alignment can significantly improve cosmetic outcome, binocular function and diplopia in appropriately selected cases.",
    },
  },
  "pediatric-ophthalmology": {
    condition: {
      name: "Paediatric refractive & amblyogenic disorders",
      alternateName: ["Childhood Eye Disorders"],
      icd10: "H53.0",
      snomed: "387742000",
      anatomy: "Eye",
      prognosis: "Amblyopia outcomes are strongly age-dependent; earliest possible detection and adherence to patching / atropine therapy give the best visual recovery.",
    },
  },
  "glaucoma": {
    condition: {
      name: "Glaucoma",
      alternateName: ["Kala Motia"],
      icd10: "H40",
      snomed: "23986001",
      anatomy: "Optic nerve",
      prognosis: "Glaucoma is chronic and progressive but manageable — timely IOP-lowering therapy and long-term follow-up preserve visual field in the majority of patients.",
    },
  },
  "cornea": {
    condition: {
      name: "Corneal disorders",
      alternateName: ["Corneal disease"],
      icd10: "H16",
      snomed: "27133005",
      anatomy: "Cornea",
      prognosis: "Outcome varies by aetiology: keratoconus is managed with cross-linking / contact lenses / DALK; dry eye is chronic but well-controlled with lipid-based tears; corneal transplants offer 5-year graft survival >80% in favourable indications.",
    },
  },
  "keratoconus": {
    condition: {
      name: "Keratoconus",
      icd10: "H18.6",
      snomed: "34943006",
      anatomy: "Cornea",
      prognosis: "Progression is halted in >95% of eyes by corneal collagen cross-linking (CXL) when performed at documented progression.",
    },
  },
  "dry-eye": {
    condition: {
      name: "Dry Eye Disease",
      alternateName: ["Keratoconjunctivitis Sicca", "DED"],
      icd10: "H04.123",
      snomed: "44653001",
      anatomy: "Ocular surface",
      prognosis: "Chronic but well-controlled with a stepwise regimen of lubricants, meibomian gland therapy, punctal plugs and — in select cases — anti-inflammatory drops.",
    },
  },
  "uveitis": {
    condition: {
      name: "Uveitis",
      icd10: "H20",
      snomed: "128473001",
      anatomy: "Uveal tract",
      prognosis: "Vision-preserving outcomes require prompt anti-inflammatory therapy and systemic workup to identify treatable underlying causes.",
    },
  },
  "rop-screening": {
    condition: {
      name: "Retinopathy of Prematurity (ROP)",
      icd10: "H35.1",
      snomed: "43022003",
      anatomy: "Retina",
      prognosis: "Aggressive ROP is a sight-threatening emergency in preterm infants; timely laser or anti-VEGF therapy preserves vision in the vast majority of appropriately screened infants.",
    },
  },
};

/**
 * Build a Schema.org `MedicalCondition` JSON-LD node for the given
 * expertise slug, or null if the slug isn't a condition (procedure-only
 * pages should stay on MedicalProcedure).  Wire the returned node into
 * the page-level `@graph`.
 */
export function medicalConditionNode(slug, pageUrl, procedureAtId) {
  const meta = CONDITION_META[slug];
  if (!meta || !meta.condition) return null;
  const c = meta.condition;
  return {
    "@type": "MedicalCondition",
    "@id": `${pageUrl}#condition`,
    name: c.name,
    alternateName: c.alternateName || undefined,
    code: [
      c.icd10 ? { "@type": "MedicalCode", codingSystem: "ICD-10", codeValue: c.icd10 } : null,
      c.snomed ? { "@type": "MedicalCode", codingSystem: "SNOMED-CT", codeValue: c.snomed } : null,
    ].filter(Boolean),
    associatedAnatomy: c.anatomy ? { "@type": "AnatomicalStructure", name: c.anatomy } : undefined,
    expectedPrognosis: c.prognosis,
    // Cross-link back to the procedure node emitted on the same page so
    // Google understands "this page is about condition X and its
    // treatment procedure Y at this clinic".
    possibleTreatment: procedureAtId ? { "@id": procedureAtId } : undefined,
  };
}
